import { DECISION_INPUT_OPTIONS } from "@/api/decision-input-values";
import { TypedFactEditor, hasTypedInputEditor, initialTypedInput, typedInputCanSave } from "./typed-fact-editor";
import { guestCountEditValue, isGuestCountValue } from "../guest-count-value";
import { CATERING_FACT_OPTIONS } from "@/api/catering-values";
import React from "react";
import requirementStyles from "./sol-requirements.module.css";
import type { FactReview, FactReviewField } from "../../../../../core/src/review/fact-review-types";
import type { SolRequiredField } from "../../../../../core/src/workflow/sol-requirement-types";
import {
  Button, Card, Dialog, FormField, Input, Select, StatusPill,
} from "@/ds";
import { FACT_TONE, Icon, cx } from "@/lib";
import { cockpit } from "@/api";
import type { Fact, OverrideFactResult, RequestDetail, ThreadTargetPage, WorkflowSwitchPreview, ThreadCommand } from "@/api";
import { useLiveInvalidation } from "@/live/live-provider";
import type { ToastAction } from "@/notifications/toast-queue";
import { useT, type TFunc } from "../../i18n";
import {
  draftContinuationMessageKey,
  factProvenanceItems,
  factSourceDisplay,
  operatorRequestReferenceLabel,
  sourceMessageOrderLabels,
} from "../screen-workspace.logic";
import { isEmptyVal, valToStr } from "../fact-value";
import { Menu, type MenuItem } from "./menu";


type Act = (
  p: Promise<unknown>,
  okTitle: string,
  okMsg?: React.ReactNode | ((result: unknown) => React.ReactNode),
  undo?: ToastAction,
) => Promise<boolean>;
const fieldLabel = (t: TFunc, path: string) => t(`field.${path}`, undefined, t("ws.fieldWithoutLabel"));
// ============================ KONVERSATION ============================
export function ThreadOperations({ messages, requestId, writerActionsBlocked, act, data, isOwner }: {
  data: RequestDetail; isOwner: boolean;
  messages: RequestDetail["messages"];
  requestId: string;
  writerActionsBlocked: boolean;
  act: Act;
}) {
  const t = useT();
  const [op, setOp] = React.useState<null | "merge" | "move" | "new">(null);
  const [target, setTarget] = React.useState("");
  const [msgId, setMsgId] = React.useState("");
  const [targetData, setTargetData] = React.useState<RequestDetail | null>(null);
  const [targetPreview, setTargetPreview] = React.useState<WorkflowSwitchPreview | null>(null);
  const [targetEngine, setTargetEngine] = React.useState("");
  const [targetError, setTargetError] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const [targets, setTargets] = React.useState<ThreadTargetPage | null>(null);
  const [targetsLoading, setTargetsLoading] = React.useState(false);
  const [targetsError, setTargetsError] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const targetReads = React.useRef(new Map<string, { promise: Promise<ThreadTargetPage>; repeat: boolean }>());
  const invalidations = useLiveInvalidation("requests", "workspace");
  React.useEffect(() => {
    const timer = window.setTimeout(() => { setSearch(query); setOffset(0); setTarget(""); }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  React.useEffect(() => {
    if (op !== "merge" && op !== "move") return;
    let active = true;
    setTargetsLoading(true); setTargetsError(false); setTarget("");
    const key = `${requestId}:${search}:${offset}`;
    let read = targetReads.current.get(key);
    if (read) read.repeat = true;
    else {
      read = { repeat: false, promise: Promise.resolve({ items: [], next_offset: null }) };
      const currentRead = read;
      read.promise = (async () => {
        let page: ThreadTargetPage;
        do { currentRead.repeat = false; page = await cockpit.listThreadTargets(requestId, search, offset); }
        while (currentRead.repeat && mounted.current);
        return page;
      })().finally(() => { targetReads.current.delete(key); });
      targetReads.current.set(key, read);
    }
    void read.promise.then(page => { if (active) setTargets(page); })
      .catch(() => { if (active) { setTargets(null); setTargetsError(true); } })
      .finally(() => { if (active) setTargetsLoading(false); });
    return () => { active = false; };
  }, [op, requestId, search, offset, invalidations, retry]);
  React.useEffect(() => {
    let active = true; setTargetData(null); setTargetPreview(null); setTargetError(false); setTargetEngine("");
    if (target) void Promise.all([cockpit.getRequest(target), isOwner ? cockpit.previewWorkflowSwitch(target) : Promise.resolve(null)])
      .then(([next, preview]) => { if (active) { setTargetData(next); setTargetPreview(preview); } })
      .catch(() => { if (active) setTargetError(true); });
    return () => { active = false; };
  }, [target, isOwner, requestId, data.request.current_revision, invalidations]);
  const crossEngine = !!targetData && (targetData.request.engine_pin.engine !== data.request.engine_pin.engine
    || targetData.request.engine_pin.profile_hash !== data.request.engine_pin.profile_hash);
  const selectedTarget = targetPreview?.options.find(o => o.engine === targetEngine);
  const targetReady = op === "new" || !targetsLoading && !targetsError && query === search
    && !!target && !!targetData && (!crossEngine || op === "merge" && isOwner && !!selectedTarget?.available);
  const menuTriggerRef = React.useRef<HTMLButtonElement>(null);
  const inbound = messages.filter((message) => message.direction === "inbound" && message.id);

  React.useEffect(() => {
    if (writerActionsBlocked) setOp(null);
  }, [writerActionsBlocked]);

  const targetOptions = [
    { value: "", label: t("ws.targetRequestPlaceholder") },
    ...(targets?.items ?? []).map((other) => {
      const reference = operatorRequestReferenceLabel(other.reference);
      const otherSubject = (other.subject || t("common.noSubject")).slice(0, 34);
      return {
        value: other.id,
        label: reference ? `${otherSubject} · ${reference}` : otherSubject,
      };
    }),
  ];
  const messageOptions = [
    { value: "", label: t("ws.messagePlaceholder") },
    ...inbound.map((message) => ({
      value: message.id as string,
      label: `${(message.subject || t("common.noSubject")).slice(0, 34)} · ${message.fromAddr || "?"}`,
    })),
  ];
  const open = (mode: "merge" | "move" | "new") => {
    if (writerActionsBlocked) return;
    setTarget("");
    setQuery(""); setSearch(""); setOffset(0); setTargets(null);
    setMsgId(inbound.length === 1 ? inbound[0].id as string : "");
    setOp(mode);
  };
  const run = () => {
    if (writerActionsBlocked) return;
    if (!targetReady) return;
    const command: ThreadCommand = { expected_revisions: { [requestId]: data.request.current_revision,
      ...(targetData ? { [targetData.request.id]: targetData.request.current_revision } : {}) } };
    if (crossEngine && selectedTarget?.profile && targetData) command.workflow_target = { engine: selectedTarget.engine,
      profile_hash: selectedTarget.profile.profile_hash, expected_revision: targetData.request.current_revision,
      expected_engine_epoch: targetData.request.engine_pin.engine_epoch, expected_source_revision: data.request.current_revision,
      idempotency_key: crypto.randomUUID() };
    if (op === "merge" && target) void act(cockpit.mergeInto(requestId, target, command), t("ws.merged"));
    else if (op === "move" && msgId && target) void act(cockpit.moveMessage(msgId, target, command), t("ws.moved"));
    else if (op === "new" && msgId) void act(cockpit.newRequestFromMessage(msgId, command), t("ws.newRequestCreated"));
    setOp(null);
  };
  const menuItems: MenuItem[] = [
    { icon: "merge", label: t("ws.merge"), onClick: () => open("merge"), disabled: writerActionsBlocked },
    { icon: "move", label: t("ws.moveMessage"), onClick: () => open("move"), disabled: writerActionsBlocked || !inbound.length },
    { sep: true },
    { icon: "plus", label: t("ws.newFromMessage"), onClick: () => open("new"), disabled: writerActionsBlocked || !inbound.length },
  ];

  return (
    <>
      <Menu
        align="right"
        triggerRef={menuTriggerRef}
        trigger={<button ref={menuTriggerRef} className="iconbtn" disabled={writerActionsBlocked} aria-label={t("ws.threadOptionsAria")}><Icon name="more" size={18} /></button>}
        items={menuItems}
      />
      <Dialog
        open={op !== null && !writerActionsBlocked}
        onClose={() => setOp(null)}
        finalFocusRef={menuTriggerRef}
        tone="navy"
        icon={<Icon name="merge" size={20} />}
        title={op === "merge" ? t("ws.mergeTitle") : op === "move" ? t("ws.moveMessage") : t("ws.newFromMessage")}
        footer={<>
          <Button variant="ghost" onClick={() => setOp(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={writerActionsBlocked || !targetReady || (op === "merge" ? !target : op === "move" ? !(msgId && target) : !msgId)} onClick={run}>{t("common.run")}</Button>
        </>}
      >
        <div className="stack-sm" style={{ paddingBottom: "var(--space-4)" }}>
          {data.request.engine_pin.engine === "single_agent" && <p>{t("workflow.paidChanges")}</p>}
          {targetError && <p role="alert">{t("workflow.loadFailed")}</p>}
          {crossEngine && <p>{t("workflow.error.engine_target_required")}</p>}
          {crossEngine && isOwner && op === "merge" && targetPreview && <FormField label={t("workflow.target")}>
            <Select value={targetEngine} onChange={e => setTargetEngine(e.target.value)} options={[{ value: "", label: t("workflow.selectTarget") },
              ...targetPreview.options.filter(o => o.available).map(o => ({ value: o.engine, label: t(`workflow.${o.engine}`) }))]} />
            <p>{t("workflow.switchConsequences")}</p>
          </FormField>}
          {(op === "move" || op === "new") && (
            <FormField label={t("ws.message")}>
              <Select value={msgId} options={messageOptions} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setMsgId(event.target.value)} />
            </FormField>
          )}
          {(op === "merge" || op === "move") && (
            <div className="stack-sm">
            <FormField label={t("workflow.searchTargets")}><Input value={query} maxLength={120}
              onChange={e => setQuery(e.target.value)} /></FormField>
            {targetsLoading && <p role="status">{t("workflow.loadingTargets")}</p>}
            {targetsError && <p role="alert">{t("workflow.targetsFailed")} <Button size="sm" onClick={() => setRetry(n => n + 1)}>{t("common.retry")}</Button></p>}
            {!targetsLoading && !targetsError && targets?.items.length === 0 && <p>{t("workflow.noTargets")}</p>}
            <FormField label={t("ws.targetRequest")}>
              <Select value={target} options={targetOptions} disabled={targetsLoading || targetsError || query !== search}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setTarget(event.target.value)} />
            </FormField>
            <div className="row">
              {offset > 0 && <Button size="sm" disabled={targetsLoading} onClick={() => setOffset(n => Math.max(0, n - 30))}>{t("workflow.previousTargets")}</Button>}
              {targets?.next_offset != null && <Button size="sm" disabled={targetsLoading} onClick={() => setOffset(targets.next_offset!)}>{t("workflow.moreTargets")}</Button>}
            </div>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}

// ============================ FAKTENLAGE ============================
/**
 * Compact fact row (#532): status dot, field, readable value, lock — evidence
 * highlighting stays the primary interaction (hover highlights, click scrolls
 * to the evidence). Source/quote/confidence/provenance sit behind one detail
 * affordance; the value edits in place with the unchanged override semantics.
 */
export function FactItem({ f, requestId, requestRevision, writerActionsBlocked, act, onEvidenceSelect, onMarkHover, sourceMessageLabel, onEditingChange, requirement, review, conflict = null, absent = false, customerPanel = false }: {
  f: Fact;
  /** Presentation only; the server recognizes the customer.type operation independently. */
  customerPanel?: boolean;
  requirement?: SolRequiredField;
  review?: FactReviewField;
  /** The check of the active refused Sol result that named this fact (#2473). */
  conflict?: string | null;
  absent?: boolean;
  requestId: string;
  requestRevision: number;
  messages: RequestDetail["messages"];
  writerActionsBlocked: boolean;
  act: Act;
  onEvidenceSelect?: (f: Fact) => void;
  onMarkHover?: (key: string | null) => void;
  sourceMessageLabel?: string | null;
  onEditingChange?: (field: string, editing: boolean) => void;
}) {
  const t = useT();
  const customerTypeEditor = customerPanel && f.fieldPath === "customer.type";
  // Only the operator-owned check is about the lock. Unlocking frees a locked extracted value;
  // an operator-entered value stays the operator's after unlocking.
  const conflictLocked = review?.conflict_locked ?? (conflict === "operator_owned" && f.isLocked && f.status !== "manually_overridden");
  const editButtonRef = React.useRef<HTMLButtonElement>(null);
  const savingRef = React.useRef(false);
  const returnFocusRef = React.useRef(false);
  const detailsId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const sol = Boolean(onEditingChange);
  const [saving, setSaving] = React.useState(false);
  const [saveFailed, setSaveFailed] = React.useState(false);
  const [editRevision, setEditRevision] = React.useState<number | null>(null);
  React.useEffect(() => {
    onEditingChange?.(f.fieldPath, editing);
    return () => onEditingChange?.(f.fieldPath, false);
  }, [editing, f.fieldPath, onEditingChange]);
  const [value, setValue] = React.useState("");
  const [typedValue, setTypedValue] = React.useState<unknown>(null);
  const [customerTypeEditBasis, setCustomerTypeEditBasis] = React.useState<{
    requestRevision: number; factVersion: number | null;
  } | null>(null);
  const editStale = editing && (sol && editRevision !== requestRevision
    || customerTypeEditor && customerTypeEditBasis !== null
      && (customerTypeEditBasis.requestRevision !== requestRevision
        || customerTypeEditBasis.factVersion !== (f.version ?? null)));
  React.useEffect(() => {
    if (!customerTypeEditor) return;
    if (editing) document.getElementById(`fact-value-${f.fieldPath}`)?.focus();
    else if (returnFocusRef.current) {
      returnFocusRef.current = false;
      editButtonRef.current?.focus();
    }
  }, [customerTypeEditor, editing, f.fieldPath]);
  const finishEditing = () => {
    returnFocusRef.current = customerTypeEditor;
    setEditing(false);
  };
  const [countKind, setCountKind] = React.useState("");
  const [countUpper, setCountUpper] = React.useState("");
  const [countApproximate, setCountApproximate] = React.useState(false);
  const [countInclusive, setCountInclusive] = React.useState(true);
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [reason, setReason] = React.useState("");
  const statusCode = FACT_TONE[f.status] ? f.status : "unknown";
  const tone = review?.tone ?? FACT_TONE[statusCode];
  const stateLabel = review ? review.states.map(state => t(`solFacts.status.${state}`)).join(" · ") : t(`status.fact.${statusCode}`);
  const displayedValue = f.fieldPath === "customer.type"
    && (!["extracted", "confirmed", "manually_overridden", "externally_resolved",
      "curated_venue_resolved"].includes(f.status)
      || !(DECISION_INPUT_OPTIONS.customerTypes as readonly unknown[]).includes(f.value)) ? null : f.value;
  const quote = f.evidenceText?.trim();
  const confidencePct = f.confidence != null ? Math.round(f.confidence * 100) : null;
  const rawOverrideReason = f.override_reason ?? f.overrideReason;
  const provenance = factProvenanceItems({
    ...f,
    source_message_label: sourceMessageLabel,
    override_reason: rawOverrideReason === "external_address_operator_confirmation"
      ? t("ws.confirmedByOperator")
      : rawOverrideReason,
  });
  const source = factSourceDisplay({ ...f, source_message_label: sourceMessageLabel });
  const sourceLabel = source ? source.label ?? t(source.labelKey, undefined, t("ws.sourceUnknown")) : null;
  const externallyResolved = f.status === "externally_resolved";
  const curatedVenue = f.status === "curated_venue_resolved";
  // Only a live match waits for a person. An address the curated store already held is final, and
  // the operator sees where it came from rather than a request to confirm it (#1642).
  const operatorConfirmableExternalAddress = externallyResolved && f.fieldPath === "event.event_address";
  React.useEffect(() => {
    if (writerActionsBlocked && !sol && !customerTypeEditor) setEditing(false);
  }, [writerActionsBlocked, sol, customerTypeEditor]);

  // Every supported correction submits the server-owned closed value shape.
  const cateringOptions = CATERING_FACT_OPTIONS[f.fieldPath as keyof typeof CATERING_FACT_OPTIONS];
  const typedCount = f.fieldPath === "event.guest_count";
  const countValue = typedCount ? guestCountEditValue(countKind, value, countUpper, countApproximate, countInclusive) : null;
  const typedInput = hasTypedInputEditor(f.fieldPath);
  const typedBoolean = (DECISION_INPUT_OPTIONS.booleanFields as readonly string[]).includes(f.fieldPath);
  const typedNumeric = (DECISION_INPUT_OPTIONS.numericFields as readonly string[]).includes(f.fieldPath);
  const typedDate = ["event.dates", "event.end_date"].includes(f.fieldPath);
  const hasValue = !(absent && typedInput && typedValue === null && !customerTypeEditor)
    && (typedInput ? typedInputCanSave(f.fieldPath, typedValue) : typedCount ? countValue !== null : cateringOptions?.multiple ? selectedValues.length > 0 : value.trim().length > 0);
  const inlineEditable = !["event.guest_count_qualifier", "catering.dietary_profile"].includes(f.fieldPath) && (typedInput || typedCount || Boolean(cateringOptions) || isEmptyVal(f.value) || ["string", "number", "boolean"].includes(typeof f.value));
  const startEditing = () => {
    if (writerActionsBlocked || !inlineEditable || (sol || customerTypeEditor) && editing) return;
    setEditRevision(requestRevision);
    setSaveFailed(false);
    if (f.fieldPath === "customer.type") {
      // Live refreshes must not rebase an in-progress correction onto another writer's fact.
      setCustomerTypeEditBasis({ requestRevision, factVersion: f.version ?? null });
    }
    if (typedInput) setTypedValue(initialTypedInput(f.fieldPath, f.value));
    const initial = Array.isArray(f.value) ? f.value : [];
    setSelectedValues(cateringOptions ? initial.filter((key) => (cateringOptions.values as readonly unknown[]).includes(key)) as string[] : []);
    setValue(cateringOptions && !cateringOptions.multiple
      ? (cateringOptions.values as readonly unknown[]).includes(f.value) ? String(f.value) : ""
      : typedBoolean ? typeof f.value === "boolean" ? String(f.value) : "" : isEmptyVal(f.value) ? "" : String(f.value));
    if (typedCount) {
      const prior = isGuestCountValue(f.value) ? f.value : null;
      setCountKind(prior?.kind ?? "");
      setValue(prior ? String(prior.kind === "range" ? prior.lower : prior.value)
        : typeof f.value === "number" ? String(f.value) : "");
      setCountUpper(prior?.kind === "range" ? String(prior.upper) : "");
      setCountApproximate(prior?.kind === "range" ? prior.approximate : false);
      setCountInclusive(prior && "inclusive" in prior ? prior.inclusive : true);
    }
    setReason("");
    setEditing(true);
  };
  const save = async () => {
    if (
      writerActionsBlocked || savingRef.current || editStale
      || !hasValue
      || (f.fieldPath === "customer.type" && !customerTypeEditBasis)
    ) return;
    if (!sol && !customerTypeEditor) setEditing(false);
    savingRef.current = true;
    setSaving(true); setSaveFailed(false);
    const saved = await act(
      cockpit.overrideFact(requestId, {
        field_path: f.fieldPath,
        value: typedInput ? typedValue : typedCount ? countValue! : cateringOptions?.multiple ? selectedValues : typedBoolean ? value === "true" : typedNumeric ? Number(value) : value,
        ...(!customerTypeEditor && reason.trim() ? { reason: reason.trim() } : {}),
        lock: true,
        expected_request_revision: f.fieldPath === "customer.type"
          ? customerTypeEditBasis!.requestRevision : sol ? editRevision! : requestRevision,
        ...(f.fieldPath === "customer.type"
          ? { expected_fact_version: customerTypeEditBasis!.factVersion } : {}),
      }),
      t("ws.factOverridden"),
      (result) => t(sol ? "workflow.recalculationRequired" : draftContinuationMessageKey(
        (result as OverrideFactResult).draft_status,
      )),
    );
    savingRef.current = false;
    setSaving(false);
    if (saved) finishEditing();
    else setSaveFailed(true);
  };
  const confirmExternalFact = () => {
    if (writerActionsBlocked || !operatorConfirmableExternalAddress) return;
    void act(
      cockpit.overrideFact(requestId, {
        field_path: f.fieldPath,
        value: f.value,
        reason: t("ws.confirmedByOperator"),
        lock: true,
        confirm_external_address: true,
        expected_request_revision: requestRevision,
      }),
      t("ws.markedConfirmed"),
      (result) => t(sol ? "workflow.recalculationRequired" : draftContinuationMessageKey(
        (result as OverrideFactResult).draft_status,
      )),
    );
  };

  const editControl = (!externallyResolved || operatorConfirmableExternalAddress) && (
    <button
      ref={editButtonRef} type="button" className="iconbtn fact-row__tool"
      disabled={writerActionsBlocked || !inlineEditable || (sol || customerTypeEditor) && editing}
      aria-label={customerTypeEditor ? `${t("ws.customer.edit")} — ${fieldLabel(t, f.fieldPath)}` : t("ws.correctFact")}
      title={!inlineEditable ? t("ws.factStructuredReadOnly") : customerTypeEditor ? t("ws.customer.edit") : undefined}
      onClick={startEditing}
    ><Icon name="pencil" size={14} /></button>
  );

  return (
    <div className={cx("fact fact-row", customerTypeEditor ? "cust-field" : review ? "" : `fact--${f.status}`, conflict && requirementStyles.attention)}
      data-testid="fact-row" data-field={f.fieldPath} data-required-open={requirement ? true : undefined}
      data-conflict={conflict ? true : undefined}
      tabIndex={-1} role={requirement || conflict ? "group" : undefined}
      aria-label={conflict ? `${fieldLabel(t, f.fieldPath)}: ${t("solFacts.conflict")}`
        : requirement ? `${fieldLabel(t, f.fieldPath)}: ${stateLabel}` : undefined}>
      {conflict && <div className={requirementStyles.marker}><Icon name="alert" size={14} />
        <span>{t("solFacts.conflict")} · {t(conflictLocked ? "solFacts.conflictLocked" : "solFacts.conflictCheck")}</span></div>}
      {requirement && !review && !conflict && <div className={requirementStyles.marker}><Icon name="info" size={14} />
        <span>{t("solFacts.status.customer_must_answer")} · {t("solFacts.required")}</span></div>}
      <div className="fact-row__line">
        <button
          type="button"
          className={cx("fact-row__hit", customerTypeEditor && "cust-field__fact-hit")}
          disabled={absent && (writerActionsBlocked || !inlineEditable)}
          onMouseEnter={() => { if (!externallyResolved) onMarkHover?.(f.fieldPath); }}
          onMouseLeave={() => { if (!externallyResolved) onMarkHover?.(null); }}
          onFocus={() => { if (!externallyResolved) onMarkHover?.(f.fieldPath); }}
          onBlur={() => { if (!externallyResolved) onMarkHover?.(null); }}
          onClick={() => {
            if (absent) startEditing();
            else if (externallyResolved) setOpen(true);
            else onEvidenceSelect?.(f);
          }}
        >
          <span className="fact-row__dot" data-tone={tone} title={stateLabel} aria-hidden="true" />
          {!review && <span className="sr-only">{stateLabel}, </span>}
          <span className="fact-row__content">
            <span className="fact-row__label">{fieldLabel(t, f.fieldPath)}</span>
            {review && <span className={requirementStyles.rowStates}>{stateLabel}</span>}
            <span className="fact-row__value">{customerTypeEditor && displayedValue === null ? <span className="muted">{t("ws.customer.empty")}</span> : valToStr(displayedValue, t)}</span>
          </span>
          <span className="sr-only">— {t(absent ? customerTypeEditor ? "ws.customer.edit" : "ws.correctFact" : externallyResolved ? "ws.factDetails" : "ws.review.showEvidence")}</span>
        </button>
        <span className="fact-row__tools">
          {!customerTypeEditor && editControl}
          {!f.authority && !absent && !operatorConfirmableExternalAddress && !(f.fieldPath === "customer.type" && f.status === "unknown") && (
            <button
              type="button"
              className={cx("iconbtn fact-row__tool", f.isLocked && "fact-row__tool--active")}
              disabled={writerActionsBlocked || customerTypeEditor && editing}
              aria-label={f.isLocked ? t("ws.unlock") : t("ws.lock")}
              onClick={() => {
                if (!writerActionsBlocked) {
                  void act(cockpit.lockFact(requestId, f.fieldPath, !f.isLocked, requestRevision), f.isLocked ? t("ws.unlocked") : t("ws.locked"));
                }
              }}
            ><Icon name={f.isLocked ? "lock" : "unlock"} size={14} /></button>
          )}
          {f.authority && !absent && <Button size="sm" variant="ghost" title={t("factAuthority.lockHint")}
            disabled={writerActionsBlocked || editing || f.authority.confirmed}
            onClick={() => { void act(cockpit.lockFact(requestId, f.fieldPath, !f.isLocked, requestRevision), t(f.isLocked ? "ws.unlocked" : "ws.locked")); }}
          >{t(f.isLocked ? "ws.unlock" : "ws.lock")}</Button>}
          {f.authority && !absent && <Button size="sm" variant="ghost"
            title={t("factAuthority.confirmHint")}
            disabled={writerActionsBlocked || editing || f.version === undefined || !f.authority.confirmed && (f.value === null || ["unknown", "ambiguous", "conflicting"].includes(f.status))}
            onClick={() => { if (f.version !== undefined) void act(cockpit.confirmFact(requestId, f.fieldPath, !f.authority!.confirmed,
              requestRevision, f.version), t(f.authority!.confirmed ? "factAuthority.withdrawn" : "factAuthority.confirmed")); }}
          >{t(f.authority.confirmed ? "factAuthority.withdraw" : "factAuthority.confirm")}</Button>}
          <button
            type="button" className="iconbtn fact-row__tool" aria-expanded={open} aria-controls={detailsId}
            aria-label={t("ws.factDetails")}
            onClick={() => setOpen((v) => !v)}
          ><Icon name={open ? "chevronUp" : "chevronDown"} size={14} /></button>
          {customerTypeEditor && editControl}
        </span>
      </div>
      {f.authority && (f.authority.locked || f.authority.confirmed) && <p className="subtle">
        {t(f.authority.confirmed ? "factAuthority.confirmedHint" : "factAuthority.lockHint")}</p>}
      {absent && !inlineEditable && <p className="subtle">{t("ws.factStructuredReadOnly")}</p>}
      {(externallyResolved || curatedVenue) && (
        <div className="fact-row__external-review" data-testid="external-fact-review">
          <StatusPill
            size="sm"
            tone={curatedVenue ? "success" : "info"}
            icon={<Icon name="external" size={12} />}
            label={review ? stateLabel : t(curatedVenue ? "status.fact.curated_venue_resolved" : "status.fact.externally_resolved")}
          />
          {operatorConfirmableExternalAddress && (
            <Button
              size="sm"
              variant="ghost"
              disabled={writerActionsBlocked}
              iconLeft={<Icon name="checkCircle" size={14} />}
              onClick={confirmExternalFact}
            >{t("ws.confirmExternalFact")}</Button>
          )}
        </div>
      )}
      {editing && (sol || customerTypeEditor || !writerActionsBlocked) && (
        <div className={customerTypeEditor ? "cust-field__edit" : "fact-row__edit"} data-testid="fact-inline-edit" aria-busy={saving}>
          {sol && !customerTypeEditor && <p>{t("solFacts.saveCost")}</p>}
          {editStale && <p role="alert">{t(customerTypeEditor ? "ws.customer.typeEditStale" : "solFacts.editStale")}</p>}
          {editStale && customerTypeEditor && <Button size="sm" variant="ghost" disabled={saving || writerActionsBlocked}
            onClick={() => {
              setCustomerTypeEditBasis({ requestRevision, factVersion: f.version ?? null });
              setEditRevision(requestRevision);
              setSaveFailed(false);
            }}>{t("ws.customer.reapplyType")}</Button>}
          {saveFailed && <p role="alert">{t(customerTypeEditor ? "ws.customer.typeSaveFailed" : "solFacts.saveFailed")}</p>}
          <fieldset disabled={saving || writerActionsBlocked || editStale} className={requirementStyles.editor}>
          <FormField label={customerTypeEditor ? fieldLabel(t, f.fieldPath) : t("ws.correctedValue")} required={!customerTypeEditor} htmlFor={`fact-value-${f.fieldPath}`}>
            {typedInput ? (
              <TypedFactEditor field={f.fieldPath} value={typedValue} onChange={setTypedValue} id={`fact-value-${f.fieldPath}`} />
            ) : typedCount ? (
              <div className="stack-sm">
                <Select id={`fact-value-${f.fieldPath}`} value={countKind}
                  options={[{ value: "", label: t("ws.count.chooseKind") }, ...["exact", "estimate", "range", "lower_bound", "upper_bound"].map((kind) =>
                    ({ value: kind, label: t(`ws.count.${kind}`) }))]}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setCountKind(event.target.value)} />
                <Input size="sm" inputMode="numeric" value={value} aria-label={t(countKind === "range" ? "ws.count.lower" : "ws.count.value")}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value)} />
                {countKind === "range" && <>
                  <Input size="sm" inputMode="numeric" value={countUpper} aria-label={t("ws.count.upper")}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCountUpper(event.target.value)} />
                  <label className="row" style={{ gap: "var(--space-2)" }}><input type="checkbox" checked={countApproximate}
                    onChange={(event) => setCountApproximate(event.target.checked)} />{t("ws.count.approximateRange")}</label>
                </>}
                {(countKind === "lower_bound" || countKind === "upper_bound") &&
                  <label className="row" style={{ gap: "var(--space-2)" }}><input type="checkbox" checked={countInclusive}
                    onChange={(event) => setCountInclusive(event.target.checked)} />{t("ws.count.inclusive")}</label>}
              </div>
            ) : cateringOptions?.multiple ? (
              <fieldset id={`fact-value-${f.fieldPath}`} aria-label={t("ws.correctedValue")}
                style={{ border: 0, padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--space-2)" }}>
                {cateringOptions.values.map((key) => (
                  <label key={key} className="row" style={{ gap: "var(--space-2)" }}>
                    <input type="checkbox" checked={selectedValues.includes(key)}
                      onChange={(event) => setSelectedValues((prior) => event.target.checked
                        ? [...prior, key] : prior.filter((value) => value !== key))} />
                    {t(`factValue.${key}`, undefined, key)}
                  </label>
                ))}
              </fieldset>
            ) : cateringOptions ? (
              <Select id={`fact-value-${f.fieldPath}`} value={value}
                options={[{ value: "", label: t("common.noInfo") }, ...cateringOptions.values.map((key) =>
                  ({ value: key, label: t(`factValue.${key}`, undefined, key) }))]}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setValue(event.target.value)} />
            ) : typedBoolean ? (
              <Select id={`fact-value-${f.fieldPath}`} value={value}
                options={[{ value: "", label: t("common.noInfo") }, { value: "true", label: t("common.yes") }, { value: "false", label: t("common.no") }]}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setValue(event.target.value)} />
            ) : (
              <Input id={`fact-value-${f.fieldPath}`} size="sm" value={value} autoFocus type={typedDate ? "date" : typedNumeric ? "number" : "text"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)} />
            )}
          </FormField>
          {!customerTypeEditor && <FormField label={t("ws.reasonOptional")} htmlFor={`fact-reason-${f.fieldPath}`}>
            <Input id={`fact-reason-${f.fieldPath}`} size="sm" value={reason} placeholder={t("ws.reasonPlaceholder")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)} />
          </FormField>}
          </fieldset>
          <div className="row-wrap" style={{ justifyContent: "flex-end", gap: "var(--space-2)" }}>
            <Button size="sm" variant="ghost" disabled={saving} onClick={finishEditing}>{t("common.cancel")}</Button>
            <Button size="sm" variant="primary" disabled={
              !hasValue || saving || writerActionsBlocked || editStale
            }
              iconLeft={<Icon name="lock" size={13} />} onClick={() => void save()}>{t(saving ? "solFacts.saving" : sol ? "solFacts.saveAndLock" : "ws.applyAndLock")}</Button>
          </div>
        </div>
      )}
      {open && (
        <dl id={detailsId} className="fact__evidence fact-row__details" data-testid="fact-details">
          <dt>{t("ws.status")}</dt>
          <dd><StatusPill size="sm" tone={tone} label={stateLabel} /></dd>
          {!absent && sourceLabel && (
            <>
              <dt>{t("ws.sourceFieldLabel")}</dt>
              <dd><span className="fact__source-line">{source?.icon && <Icon name={source.icon} size={14} />}{sourceLabel}</span></dd>
            </>
          )}
          {quote && (
            <>
              <dt>{t("ws.originalFieldLabel")}</dt>
              <dd className="fact__quote">
                {onEvidenceSelect ? (
                  <button
                    type="button"
                    className="linkbtn"
                    aria-label={t("ws.review.showEvidence")}
                    onClick={() => onEvidenceSelect(f)}
                  >
                    “{quote}”
                  </button>
                ) : `“${quote}”`}
              </dd>
            </>
          )}
          {confidencePct != null && (
            <>
              <dt>{t("ws.confidence")}</dt>
              <dd>{t("ws.confidencePct", { n: confidencePct })}</dd>
            </>
          )}
          {provenance.map((item) => (
            <React.Fragment key={item.key}>
              <dt>{t(item.labelKey)}</dt>
              <dd className="neo-mono">{item.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}

// Presentation order uses contract field identities only; values and provenance stay server-owned.
const FACT_REVIEW_GROUPS = [
  { key: "event", titleKey: "ws.factGroupEvent", fields: [
    "event.dates", "event.start_time", "event.program_start_time", "event.guest_arrival_time", "event.end_time", "event.end_date",
    "event.guest_count", "event.guest_count_qualifier", "event.guest_count_deferred", "event.venue_name", "event.event_address",
    "event.event_name", "event.event_type", "event.is_multiday", "event.is_outdoor",
  ] },
  { key: "food", titleKey: "ws.factGroupFood", fields: [
    "catering.types", "catering.food_direction", "catering.purpose", "catering.service_form",
    "catering.course_count", "catering.culinary_style", "catering.sweet_requested",
    "catering.dietary_profile", "catering.dietary_requirements", "catering.dietary_guest_counts",
    "catering.drinks_from_neo", "catering.budget_total_eur", "catering.budget",
  ] },
  { key: "logistics", titleKey: "ws.factGroupLogistics", fields: [
    "logistics.setup_access_time", "logistics.delivery_window", "logistics.ground_level_delivery", "logistics.elevator_available", "logistics.elevator_suitable",
    "logistics.kitchen_available", "logistics.kitchen_equipment", "logistics.seating_type", "logistics.seating_availability",
    "catering.service_staff_required", "catering.tableware_required", "catering.tableware_type",
    "catering.coffee_setup", "catering.buffet_tables_required", "logistics.grill_available_on_site",
    "logistics.grill_requested_from_neo", "logistics.grillmaster_requested", "logistics.cook_requested",
    "logistics.weather_protection_available", "logistics.pavilion_requested", "logistics.hall",
    "logistics.stand_number", "logistics.stand_floors", "logistics.trade_fair_equipment",
  ] },
  { key: "other", titleKey: "ws.factGroupOther", fields: [] },
];
const FACT_REVIEW_ORDER = new Map(FACT_REVIEW_GROUPS.flatMap((group, groupIndex) => (
  group.fields.map((field, order) => [field, { groupIndex, order }] as const)
)));

export function FactsPanel({ requestId, requestRevision, facts, review, assessmentPending = false, messages, writerActionsBlocked, act, onEvidenceSelect, onMarkHover, onEditingChange, requirements, refused = false, historicalRequirements, conflict, onlyRequired = false, onOnlyRequiredChange, onOpenQuestionDraft, onOpenTasks }: {
  requestId: string;
  requestRevision: number;
  facts: Fact[];
  review?: FactReview | null;
  assessmentPending?: boolean;
  onOpenTasks?: () => void;
  requirements?: SolRequiredField[] | null;
  refused?: boolean;
  historicalRequirements?: import("@/api").WorkflowDetail["historical_requirements"];
  /** The fields and check the active refused Sol result names (#2473). */
  conflict?: { fields: readonly string[]; check: string } | null;
  onlyRequired?: boolean;
  onOnlyRequiredChange?: (value: boolean) => void;
  onOpenQuestionDraft?: () => void;
  onEditingChange?: (field: string, editing: boolean) => void;
  messages: RequestDetail["messages"];
  writerActionsBlocked: boolean;
  act: Act;
  onEvidenceSelect?: (f: Fact) => void;
  onMarkHover?: (key: string | null) => void;
}) {
  const t = useT();
  const [retainedEditors, setRetainedEditors] = React.useState<ReadonlyMap<string, Fact>>(new Map());
  const reviewed = new Map((review?.fields ?? []).map(field => [field.field_path, field]));
  const attention = assessmentPending ? [] : (review?.fields ?? []).filter(field => field.conflict_check || field.tasks.length);
  const required = new Map((requirements ?? []).map(item => [item.field_path, item]));
  const conflicting = new Set(conflict?.fields ?? []);
  const persisted = new Set(facts.map(f => f.fieldPath));
  const rows = [...facts, ...(requirements ?? []).filter(item => !persisted.has(item.field_path))
    .map(item => ({ fieldPath: item.field_path, value: null, status: "unknown", isLocked: false } as Fact))];
  for (const field of reviewed.keys()) if (!rows.some(row => row.fieldPath === field))
    rows.push({ fieldPath: field, value: null, status: "unknown", isLocked: false });
  // A named field without a stored fact still gets a row to highlight.
  for (const field of conflicting) if (!rows.some(row => row.fieldPath === field))
    rows.push({ fieldPath: field, value: null, status: "unknown", isLocked: false });
  if (!rows.some(row => row.fieldPath === "event.event_type"))
    rows.push({ fieldPath: "event.event_type", value: null, status: "unknown", isLocked: false });
  for (const [field, fact] of retainedEditors) if (!rows.some(row => row.fieldPath === field)) rows.push(fact);
  const rowBasis = React.useRef(rows); rowBasis.current = rows;
  const trackEditing = React.useCallback((field: string, editing: boolean) => {
    setRetainedEditors(previous => {
      if (previous.has(field) === editing) return previous;
      const next = new Map(previous), fact = rowBasis.current.find(row => row.fieldPath === field);
      if (editing && fact) next.set(field, fact); else next.delete(field);
      return next;
    });
    onEditingChange?.(field, editing);
  }, [onEditingChange]);
  const visibleRows = onlyRequired && (requirements != null || conflicting.size > 0)
    ? rows.filter(row => required.has(row.fieldPath) || conflicting.has(row.fieldPath) || reviewed.get(row.fieldPath)?.tasks.length || retainedEditors.has(row.fieldPath)) : rows;
  const counts = facts.reduce<Record<string, number>>((a, f) => { a[f.status] = (a[f.status] || 0) + 1; return a; }, {});
  const groupId = React.useId();
  const sourceMessageLabels = React.useMemo(() => (
    sourceMessageOrderLabels(messages, (n) => t("ws.sourceEmailMessage", { n }))
  ), [messages, t]);
  const groups = (() => {
    const result = FACT_REVIEW_GROUPS.map((group) => ({ ...group, facts: [] as Fact[] }));
    for (const fact of visibleRows) {
      const groupIndex = FACT_REVIEW_ORDER.get(fact.fieldPath)?.groupIndex ?? result.length - 1;
      result[groupIndex].facts.push(fact);
    }
    for (const group of result) {
      group.facts.sort((a, b) => (
        (FACT_REVIEW_ORDER.get(a.fieldPath)?.order ?? 0) - (FACT_REVIEW_ORDER.get(b.fieldPath)?.order ?? 0)
      ));
    }
    return result.filter((group) => group.facts.length > 0);
  })();

  const revealFact = (field: string) => {
    // Summary links target the one mounted editor, preserving local input and evidence controls.
    if (retainedEditors.size > 0) return;
    const target = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[data-testid="fact-row"]') ?? [])
      .find(row => row.dataset.field === field);
    target?.scrollIntoView({ block: "nearest" }); target?.focus({ preventScroll: true });
  };
  const panelRef = React.useRef<HTMLDivElement>(null);
  const renderStates = (field: FactReviewField) => <span className={requirementStyles.states}>
    {field.states.map(state => t(`solFacts.status.${state}`)).join(" · ")}
  </span>;
  return (
    <div ref={panelRef}><Card title={t("ws.facts")} className="ws-facts-section"
      actions={!review ? <div className="row-wrap">
        {counts.conflicting ? <StatusPill size="sm" tone="danger" label={t("ws.conflictCount", { n: counts.conflicting })} /> : null}
        {counts.ambiguous ? <StatusPill size="sm" tone="warning" label={t("ws.ambiguousCount", { n: counts.ambiguous })} /> : null}
      </div> : undefined}>
      {attention.length > 0 && <section className={requirementStyles.layer} aria-labelledby={`${groupId}-attention`}>
        <h3 id={`${groupId}-attention`}>{t("solFacts.attention")}</h3>
        {attention.map(field => <div key={field.field_path} className={requirementStyles.attention}>
          <Button variant="ghost" size="sm" disabled={retainedEditors.size > 0} onClick={() => revealFact(field.field_path)} iconRight={<Icon name="arrowRight" size={14} />}>
            {fieldLabel(t, field.field_path)}
          </Button>
          {renderStates(field)}
          {field.conflict_check && <p>{t("solFacts.conflict")} · {t(field.conflict_locked ? "solFacts.conflictLocked" : "solFacts.conflictCheck")}</p>}
          {[...new Map(field.tasks.map(task => [`${task.reason_code}:${task.settled_at_send}`, task])).values()].map(task =>
            <p key={`${task.reason_code}:${task.settled_at_send}`}>{t(task.settled_at_send ? "solFacts.taskSettledAtSend" : "solFacts.blockingTask",
              { task: t(`workflow.task.${task.reason_code}`) })}</p>)}
          {field.tasks.length > 0 && onOpenTasks && <Button size="sm" variant="ghost" onClick={onOpenTasks}>{t("solGuide.action.tasks")}</Button>}
        </div>)}
      </section>}
      {requirements !== undefined && <section className={requirementStyles.layer} aria-labelledby={`${groupId}-customer`}>
        <h3 id={`${groupId}-customer`}>{t("solFacts.customerAnswers")}</h3>
        {refused ? <div>
          <p role="status">{t(historicalRequirements ? "solRefusal.historicalRequirements" : "solRefusal.noHistoricalRequirements",
            { revision: historicalRequirements?.request_revision ?? "" })}</p>
          {historicalRequirements && <ul>{historicalRequirements.fields.map(item => <li key={item.field_path}>
            {fieldLabel(t, item.field_path)} · {t(`solFacts.state.${item.state}`)}
          </li>)}</ul>}
          {historicalRequirements?.fields.length === 0 && <p>{t("solRefusal.noPreviousGaps")}</p>}
        </div> : requirements === null ? <p role="status">{t("solFacts.unavailable")}</p> : !requirements.length ? <p>{t("solFacts.complete")}</p>
          : requirements.map(item => {
            const field = reviewed.get(item.field_path);
            return <div key={item.field_path} className={requirementStyles.question} data-required-open>
              <Button variant="ghost" size="sm" disabled={retainedEditors.size > 0} onClick={() => revealFact(item.field_path)} iconRight={<Icon name="arrowRight" size={14} />}>
                {fieldLabel(t, item.field_path)}
              </Button>
              {field && renderStates(field)}
              <p className={requirementStyles.questionStatus}>
                {field?.question === "asked" && <Icon name="checkCircle" size={14} />}
                {t(`solFacts.question.${field?.question ?? "no_current_draft"}`)}
              </p>
              <p>{t("solFacts.doesNotBlock")}</p>
            </div>;
          })}
        {onOpenQuestionDraft && <Button size="sm" variant="ghost" disabled={retainedEditors.size > 0}
          onClick={onOpenQuestionDraft}>{t("solFacts.questionDraft")}</Button>}
      </section>}
      {review && <h3 className={requirementStyles.allTitle}>{t("solFacts.allFacts")}</h3>}
      {requirements !== undefined && (Boolean(requirements?.length) || onlyRequired) && <div className={requirementStyles.assessment}>
        <Button size="sm" variant="ghost" disabled={retainedEditors.size > 0} aria-pressed={onlyRequired}
          onClick={() => onOnlyRequiredChange?.(!onlyRequired)}>{t(onlyRequired ? "solFacts.all" : "solFacts.filter")}</Button>
      </div>}
      {rows.length === 0 && requirements === undefined && <p className="subtle">{t("ws.noFactsYet")}</p>}
      {groups.map((group) => (
        <section key={group.key} className="fact-group" aria-labelledby={`${groupId}-${group.key}`}>
          <h4 id={`${groupId}-${group.key}`} className="fact-group__title">{t(group.titleKey)}</h4>
          {group.facts.map((f) => (
            <FactItem
              key={f.fieldPath}
              f={f}
              onEditingChange={onEditingChange ? trackEditing : undefined}
              requirement={required.get(f.fieldPath)}
              review={reviewed.get(f.fieldPath)}
              conflict={conflicting.has(f.fieldPath) ? conflict!.check : null}
              absent={!persisted.has(f.fieldPath)}
              requestId={requestId}
              requestRevision={requestRevision}
              messages={messages}
              writerActionsBlocked={writerActionsBlocked}
              act={act}
              onEvidenceSelect={onEvidenceSelect}
              onMarkHover={onMarkHover}
              sourceMessageLabel={f.source_message_id ? sourceMessageLabels.get(f.source_message_id) ?? null : null}
            />
          ))}
        </section>
      ))}
    </Card></div>
  );
}
